import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import {flushSync} from 'react-dom';
import {useTranslation} from 'react-i18next';
import {cn} from '@chekinapp/ui';
import {countNights} from '@chekin/shared/guest-form';
import {measurePropertyHeaderFrame, useArrivalProperty} from 'components/arrival';
import {BookingCard} from 'components/EntranceScreen/EntranceBookingCard';
import {formatStayDate} from 'components/EntranceScreen/formatStayDate';
import {useBrandingTheme} from 'context/brandingTheme';
import {isDesktopViewport} from 'utils/isDesktopViewport';
import {prefersReducedMotion} from 'utils/prefersReducedMotion';
import genericPhoto from 'assets/img/property-link-generic.webp';
import type {HandoffReservation} from '../../useBookingFoundHandoffStore';
import styles from './BookingFoundScreen.module.css';

function toFirstName(fullName?: string) {
  const first = fullName?.trim().split(/\s+/)[0];
  if (!first) return '';

  return first.charAt(0).toUpperCase() + first.slice(1).toLowerCase();
}

const CARD_FIELDS_DELAY_S = 0.2;
const GROW_EASE = 'cubic-bezier(.65,0,.25,1)';
const LEAVE_EASE = 'cubic-bezier(.4,0,1,1)';
const FADE_EASE = 'cubic-bezier(.4,0,.2,1)';
const GROW_MS = 700;
const GROW_DELAY_MS = 120;
const FADE_MS = 180;
const INTRO_FADE_MS = 230;
const CARD_SLIDE_MS = 700;

function matchHeaderPhotoZoom(photo: HTMLElement) {
  const headerZoom = document
    .querySelector('[data-entrance="photo"]')
    ?.getAnimations()[0];
  const zoom = photo
    .getAnimations()
    .find(animation => (animation as CSSAnimation).animationName?.includes('photo-loop'));

  if (headerZoom && zoom) {
    zoom.currentTime = headerZoom.currentTime;
  }
  if (!isDesktopViewport()) {
    zoom?.pause();
  }
}

function leaveSearchPage(title: HTMLElement) {
  const box = document.querySelector('[data-entrance="property-card"]');
  const welcome = document.querySelector('[data-property-welcome]');
  const name = document.querySelector('[data-property-name]');
  const animations: Animation[] = [];

  if (box) {
    animations.push(
      box.animate(
        [
          {transform: 'none', opacity: 1},
          {transform: 'translateX(-28px)', opacity: 0},
        ],
        {duration: 220, easing: LEAVE_EASE, fill: 'forwards'},
      ),
    );
  }
  if (welcome) {
    animations.push(
      welcome.animate(
        [
          {transform: 'none', opacity: 1},
          {transform: 'translateY(-14px)', opacity: 0},
        ],
        {duration: 520, easing: FADE_EASE, fill: 'forwards'},
      ),
    );
  }
  if (name) {
    const from = name.getBoundingClientRect();
    const to = title.getBoundingClientRect();
    const scale = from.height / to.height;

    animations.push(
      name.animate(
        [
          {transformOrigin: '0 0', transform: 'none', opacity: 1},
          {
            transformOrigin: '0 0',
            transform: `translate(${to.left - from.left}px, ${
              to.top - from.top
            }px) scale(${1 / scale})`,
            opacity: 0,
          },
        ],
        {duration: 405, easing: LEAVE_EASE, fill: 'forwards'},
      ),
    );
    title.animate(
      [
        {
          transformOrigin: '0 0',
          transform: `translate(${from.left - to.left}px, ${
            from.top - to.top
          }px) scale(${scale})`,
          opacity: 0,
        },
        {opacity: 1, offset: 0.35},
        {transformOrigin: '0 0', transform: 'none', opacity: 1},
      ],
      {duration: 900, easing: GROW_EASE, fill: 'backwards'},
    );
  }

  return () => animations.forEach(animation => animation.cancel());
}

function slideFrom(element: HTMLElement, from: DOMRect) {
  const to = element.getBoundingClientRect();
  const y = from.top - to.top;

  if (Math.abs(from.width - to.width) + Math.abs(y) < 1) return;

  element.animate(
    [
      {transform: `translateY(${y}px)`, maxWidth: `${from.width}px`},
      {transform: 'none', maxWidth: `${to.width}px`},
    ],
    {duration: CARD_SLIDE_MS, easing: GROW_EASE},
  );
}

function growToFullScreen(sheet: HTMLElement) {
  if (prefersReducedMotion()) return;

  const {top, left, width, height} = sheet.getBoundingClientRect();
  Array.from(sheet.children).forEach(child =>
    child.animate([{opacity: 1}, {opacity: 0}], {duration: FADE_MS, fill: 'forwards'}),
  );
  Object.assign(sheet.style, {
    inset: 'auto',
    top: `${top}px`,
    left: `${left}px`,
    width: `${width}px`,
    height: `${height}px`,
    maxHeight: 'none',
    overflow: 'hidden',
    transform: 'none',
    transformOrigin: '0 0',
    animation: 'none',
  });
  sheet.animate(
    [
      {transform: 'none'},
      {
        transform: `translate(${-left}px, ${-top}px) scale(${
          window.innerWidth / width
        }, ${window.innerHeight / height})`,
        borderRadius: '0',
      },
    ],
    {duration: GROW_MS, delay: GROW_DELAY_MS, easing: GROW_EASE, fill: 'forwards'},
  );
}

type Props = {
  reservation: HandoffReservation;
  guestName?: string;
  propertyName?: string;
  isNew?: boolean;
  isLeaving?: boolean;
  isGrowing?: boolean;
  isStraightToHome?: boolean;
  isBehindEntrance?: boolean;
  isCovered?: boolean;
  guests?: number;
  children?: ReactNode;
};

function BookingFoundScreen({
  reservation,
  guestName,
  propertyName,
  isNew,
  isLeaving,
  isGrowing,
  isStraightToHome,
  isBehindEntrance,
  isCovered,
  guests: confirmedGuests,
  children,
}: Props) {
  const {t, i18n} = useTranslation();
  const liveProperty = useArrivalProperty();
  const {data: branding} = useBrandingTheme();
  const [property] = useState(() => ({
    name: liveProperty.name,
    city: liveProperty.city,
    country: liveProperty.country,
  }));
  const [headerFrame] = useState(measurePropertyHeaderFrame);
  const [startedAt] = useState(Date.now);
  const [sheetHeight, setSheetHeight] = useState(0);
  const [isLifted, setIsLifted] = useState(false);
  const stageRef = useRef<HTMLDivElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  const photoRef = useRef<HTMLImageElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const firstName = toFirstName(guestName);
  const isConfirming = Boolean(children);

  const nights = countNights(reservation.check_in_date, reservation.check_out_date);
  const guests =
    confirmedGuests ??
    reservation.guest_group?.number_of_guests ??
    reservation.guest_group?.known_number_of_guests;
  const checkIn = formatStayDate(reservation.check_in_date, i18n.language);
  const checkOut = formatStayDate(reservation.check_out_date, i18n.language);

  const fields = [
    {label: t('entrance_booking'), value: reservation.booking_reference || undefined},
    {label: t('entrance_stay'), value: `${checkIn} – ${checkOut}`},
    {
      label: t('guests'),
      value: guests ? t('guests_count', {count: guests}) : undefined,
      flashOnChange: true,
    },
    {
      label: t('entrance_nights'),
      value: nights ? t('nights_count', {count: nights}) : undefined,
    },
  ].filter(field => field.value);

  useLayoutEffect(function continueFromTheSearchPage() {
    if (photoRef.current) {
      matchHeaderPhotoZoom(photoRef.current);
    }
    if (titleRef.current && isDesktopViewport() && !prefersReducedMotion()) {
      return leaveSearchPage(titleRef.current);
    }
  }, []);

  useLayoutEffect(
    function reserveRoomForTheSheet() {
      const sheet = sheetRef.current;
      if (!isConfirming || !sheet) return;

      const measure = () => setSheetHeight(sheet.offsetHeight);
      measure();
      const observer = new ResizeObserver(measure);
      observer.observe(sheet);
      return () => observer.disconnect();
    },
    [isConfirming],
  );

  useEffect(
    function liftTheCardOnceTheIntroFades() {
      if (!isConfirming) return;

      const isStill = prefersReducedMotion() || isCovered;
      const timeout = setTimeout(
        () => {
          const card = stageRef.current?.querySelector<HTMLElement>(
            '[data-entrance="card"]',
          );
          const from = card?.getBoundingClientRect();

          flushSync(() => setIsLifted(true));
          if (card && from && !isStill) {
            slideFrom(card, from);
          }
        },
        isStill ? 0 : INTRO_FADE_MS,
      );

      return () => clearTimeout(timeout);
    },
    [isConfirming, isCovered],
  );

  useEffect(
    function growIntoTheHome() {
      if (isGrowing && sheetRef.current) {
        growToFullScreen(sheetRef.current);
      }
    },
    [isGrowing],
  );

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="booking-found-title"
      aria-live="polite"
      data-booking-found
      className={cn(
        styles.screen,
        isBehindEntrance && styles.fromEntrance,
        isCovered && styles.paused,
        isConfirming && styles.confirming,
        isLifted && styles.lifted,
        isStraightToHome && styles.straight,
        isLeaving && styles.leaving,
      )}
      style={{...headerFrame, '--sheet-height': `${sheetHeight}px`} as CSSProperties}
    >
      <div className={styles.scene}>
        <div className={styles.backdrop}>
          <img ref={photoRef} src={genericPhoto} alt="" className={styles.photo} />
        </div>

        <div ref={stageRef} className={styles.stage}>
          <div className={styles.intro}>
            <svg viewBox="0 0 52 52" aria-hidden="true" className={styles.mark}>
              <circle className={styles.circle} cx="26" cy="26" r="24" />
              <g className={styles.glass}>
                <circle cx="24" cy="24" r="7.5" />
                <path d="m29.5 29.5 6 6" />
              </g>
              <path
                className={styles.house}
                d="M15 26 26 17l11 9v10.5a1.5 1.5 0 0 1-1.5 1.5h-19A1.5 1.5 0 0 1 15 36.5z"
              />
              <path className={styles.door} d="M22.5 38v-6.5h7V38" />
            </svg>

            <p
              className={cn(
                styles.eyebrow,
                'mb-1.5 text-[12px] font-bold uppercase tracking-[0.1em] text-[color:var(--chekin-on-scene-text-eyebrow)]',
              )}
            >
              {isNew
                ? t('arrival_booking_created_eyebrow')
                : t('arrival_booking_found_eyebrow')}
            </p>
            <h2
              ref={titleRef}
              id="booking-found-title"
              className={cn(
                styles.title,
                'text-[32px] font-bold leading-[1.15] tracking-[-0.02em] desk:text-[46px]',
              )}
            >
              {firstName
                ? t('welcome_named', {name: firstName})
                : t('arrival_welcome_plain')}
            </h2>
          </div>

          <BookingCard
            name={propertyName || property.name || ''}
            location={[property.city, property.country].filter(Boolean).join(', ')}
            logo={branding?.splash_screen_logo}
            fields={fields}
            startedAt={startedAt}
            startDelay={CARD_FIELDS_DELAY_S}
            className={styles.card}
          />

          <p
            className={cn(
              styles.opening,
              'mt-5 flex items-center gap-2 text-[14px] font-semibold text-[color:var(--chekin-on-scene-text-eyebrow)]',
            )}
          >
            {t('arrival_opening_your_booking')}
            <i
              className={cn(
                styles.progress,
                'relative block h-[3px] w-14 overflow-hidden rounded-full bg-white/20',
              )}
            />
          </p>
        </div>

        {children && (
          <div ref={sheetRef} className={styles.sheet}>
            {children}
          </div>
        )}
      </div>
    </div>
  );
}

export {BookingFoundScreen};
